import * as React from 'react';
import { useSelector } from 'react-redux';
import { useVisitedOffersContext } from '@cian/frontend-visited-offers-widget';

import {
  getNewbuildingPromoSnippetTitle,
  getNewbuildingExtra,
  getIsVisiblePromoSnippetReviews,
  getNewbuildingPromoSnippetNewbuilding,
  getIsNewbuildingPromoSnippetDisplayed,
  getIsNewbuildingPromoSnippetEnabled,
  getNewbuildingPromoSnippetExtensionFields,
} from '../../selectors/newbuildingPromoSnippet';

import { Call } from '../Call';
import { RoomCounts } from '../OffersContainer/RoomCounts';
import { FavoriteButton } from '../OffersContainer/FavoriteButton';
import { ReviewLinkContainer } from '../ReviewLinkContainer';

import { Image } from '../../components/Image';
import { PromoSnippetTitle } from '../../components/PromoSnippetTitle';
import { PromoSnippetLayout } from '../../components/PromoSnippetLayout';
import { NewbuildingFeatures } from '../../components/NewbuildingFeatures';
import { PromoSnippetAddress } from '../../components/PromoSnippetAddress';
import { PromoSnippetCarousel } from '../../components/PromoSnippetCarousel';

import {
  PromoSnippetFavoriteActionNames,
  trackPromoSnippetShow,
  trackPromoSnippetJkClick,
  trackPromoSnippetDeveloperFlatsClick,
  trackPromoSnippetPhoneNumberShow,
  trackPromoSnippetFeaturesClick,
  trackPromoSnippetContactsShow,
  trackPromoSnippetReviewsClick,
} from './tracking';
import { getCurrentJsonQuery } from '../../selectors/location';
import { INewbuilding, INewbuildingPromoSnippetExtensionFields } from '../../types/newbuilding';
import { LabelsContainer } from '../LabelsContainer';
import { MessageButtonContainer } from '../MessageButtonContainer';
import { CardLabelList } from '../../components/CardLabelList';
import { MetroInfo } from '../../components/MetroInfo';
import { Advertises } from '../OffersContainer/Advertise';
import { BuilderInfo } from '../OffersContainer/BuilderInfo';

export function PromoSnippetContainer() {
  const newbuilding = useSelector(getNewbuildingPromoSnippetNewbuilding);
  const isNewbuildingPromoSnippetEnabled = useSelector(getIsNewbuildingPromoSnippetEnabled);
  const isNewbuildingPromoSnippetDisplayed = useSelector(getIsNewbuildingPromoSnippetDisplayed);
  const extensionFields = useSelector(getNewbuildingPromoSnippetExtensionFields);

  const isSnippetDisplayed = isNewbuildingPromoSnippetEnabled && isNewbuildingPromoSnippetDisplayed;

  if (!newbuilding || !isSnippetDisplayed || !extensionFields) {
    return null;
  }

  return <PromoSnippetContentContainer />;
}

export function PromoSnippetContentContainer() {
  const newbuilding = useSelector(getNewbuildingPromoSnippetNewbuilding) as INewbuilding;
  const extensionFields = useSelector(
    getNewbuildingPromoSnippetExtensionFields,
  ) as INewbuildingPromoSnippetExtensionFields;
  const {
    id,
    url,
    specialStatusDisplay,
    undergroundInfo,
    addressLine,
    isFavorite,
    gaLabels,
    fromDeveloperPropsLink,
    fromDeveloperCountsDisplay,
    fromAgentsCountsDisplay,
    fromAgentsPropsLink,
    fromAgentsPropsCount,
    fromDevelopersRooms,
    labels,
    offerTypes,
    firstMaterial,
    builders,
  } = newbuilding;

  const { images } = extensionFields;

  const title = useSelector(getNewbuildingPromoSnippetTitle);
  const newbuildingExtra = useSelector(getNewbuildingExtra);
  const jsonQuery = useSelector(getCurrentJsonQuery);
  const isVisibllePromoSnippetReviews = useSelector(getIsVisiblePromoSnippetReviews);
  const isFromAgentsAndOwnersFilter = jsonQuery.from_developer?.value === false;

  const { setOfferVisited } = useVisitedOffersContext();

  React.useEffect(() => {
    trackPromoSnippetShow(id, jsonQuery);
  }, []);

  const [hovered, setHovered] = React.useState(false);

  const handleJkClick = () => {
    trackPromoSnippetJkClick(id, jsonQuery);

    setOfferVisited(id);
  };

  const handleReviewsClick = () => {
    trackPromoSnippetReviewsClick(id, jsonQuery);
  };

  const handleFeaturesClick = (action: string) => {
    trackPromoSnippetFeaturesClick(id, action, jsonQuery);
  };

  const handleFlatClick = (countDisplay: string) => {
    trackPromoSnippetDeveloperFlatsClick({ newbuildingId: id, room: countDisplay, jsonQuery });
  };

  const handlePhoneNumberShow = () => {
    trackPromoSnippetPhoneNumberShow(id, gaLabels.full, jsonQuery);
  };

  const handleContactsShow = () => {
    trackPromoSnippetContactsShow(id, gaLabels.full, newbuildingExtra, jsonQuery);
  };

  return (
    <PromoSnippetLayout
      onHover={setHovered}
      url={url}
      onClick={handleJkClick}
      title={<PromoSnippetTitle title={title} url={url} onClick={handleJkClick} hovered={hovered} />}
      specialStatusDisplay={specialStatusDisplay}
      offerTypes={offerTypes}
      firstMaterial={firstMaterial && firstMaterial.name}
      preview={
        images ? (
          <PromoSnippetCarousel
            url={newbuilding.url}
            key={newbuilding.id}
            features={
              newbuilding.newbuildingFeaturesV2 && (
                <NewbuildingFeatures
                  features={newbuilding.newbuildingFeaturesV2}
                  url={newbuilding.url}
                  onClick={handleFeaturesClick}
                />
              )
            }
            labels={<LabelsContainer offerId={newbuilding.id} promo />}
            title={title}
            images={images}
          />
        ) : (
          <Image />
        )
      }
      reviews={
        isVisibllePromoSnippetReviews && (
          <ReviewLinkContainer
            onClick={handleReviewsClick}
            newbuildingId={newbuilding.id}
            newbuildingUrl={url}
            newbuilding={newbuilding}
          />
        )
      }
      underground={undergroundInfo && <MetroInfo undergroundInfo={undergroundInfo} regionId={newbuilding.regionId} />}
      address={addressLine && <PromoSnippetAddress newbuilding={newbuilding} />}
      labels={<CardLabelList labels={labels} />}
      advertises={
        <Advertises
          fromDevelopersRooms={fromDevelopersRooms}
          fromAgentsCountsDisplay={fromAgentsCountsDisplay}
          fromAgentsPropsCount={fromAgentsPropsCount}
          fromAgentsPropsLink={fromAgentsPropsLink}
          isFromAgentsAndOwnersFilter={isFromAgentsAndOwnersFilter}
          isPromoSnippet
          onOfferClick={handleFlatClick}
        />
      }
      callButton={<Call onShowContacts={handleContactsShow} onShowPhone={handlePhoneNumberShow} />}
      messageButton={<MessageButtonContainer newbuilding={newbuilding} buttonSize="M" isPromoSnippet={true} />}
      favoriteButton={
        <FavoriteButton
          isFavorite={Boolean(isFavorite)}
          newbuildingId={id}
          gaLabelFull={gaLabels.full}
          analyticsActionAddName={PromoSnippetFavoriteActionNames.Add}
          analyticsActionRemoveName={PromoSnippetFavoriteActionNames.Remove}
        />
      }
      roomCounts={
        <RoomCounts
          fromDeveloperPropsLink={fromDeveloperPropsLink}
          fromDeveloperCountsDisplay={fromDeveloperCountsDisplay}
          fromAgentsCountsDisplay={fromAgentsCountsDisplay}
          fromAgentsPropsLink={fromAgentsPropsLink}
          fromDevelopersRooms={fromDevelopersRooms}
          isFromAgentsAndOwnersFilter={isFromAgentsAndOwnersFilter}
        />
      }
      builder={<BuilderInfo builders={builders} isPromoSnippet />}
    />
  );
}
